import type { AgentBuilderFormData } from './index';

export const AGENT_BUILDER_ONBOARDING_FORM_ID = 'agent_builder_onboarding';

export const ONBOARDING_ASSISTANT_SYNC_FIELDS = [
  'agentName',
  'agentTagline',
  'websiteDomain',
  'notificationEmail',
  'personalityPrompt',
  'basicKnowledge',
] as const;

export type OnboardingAssistantSyncField =
  (typeof ONBOARDING_ASSISTANT_SYNC_FIELDS)[number];

export type OnboardingAssistantFieldSource = 'initial' | 'manual' | 'assistant';

export type OnboardingAssistantFieldPatch = Partial<
  Record<OnboardingAssistantSyncField, string>
>;

export type OnboardingAssistantFieldSyncState = Record<
  OnboardingAssistantSyncField,
  { source: OnboardingAssistantFieldSource }
>;

export interface OnboardingAssistantFieldResult {
  status: 'applied' | 'ignored';
  code?: 'manual_override' | 'unchanged' | 'invalid_value';
}

export interface OnboardingAssistantSyncPatchResult {
  status: 'success' | 'error';
  appliedFields: OnboardingAssistantSyncField[];
  skippedFields: OnboardingAssistantSyncField[];
  results: Partial<
    Record<OnboardingAssistantSyncField, OnboardingAssistantFieldResult>
  >;
  message?: string;
  confirmOverwrite?: boolean;
}

export interface OnboardingAssistantContextField {
  label: string;
  value: string;
  source: OnboardingAssistantFieldSource;
  lockedByManualInput: boolean;
}

export interface OnboardingAssistantContext {
  onboarding_form: {
    form_id: typeof AGENT_BUILDER_ONBOARDING_FORM_ID;
    surface: 'agent_builder_step2';
    active_step_id: string;
    supported_fields: OnboardingAssistantSyncField[];
    fields: Record<
      OnboardingAssistantSyncField,
      OnboardingAssistantContextField
    >;
    formatting_rules: {
      websiteDomain: string;
      notificationEmail: string;
    };
    effective_model_id?: string;
    available_model_ids?: string[];
  };
}

export const ONBOARDING_ASSISTANT_FIELD_LABELS: Record<
  OnboardingAssistantSyncField,
  string
> = {
  agentName: 'Agent name',
  agentTagline: 'Agent tagline',
  websiteDomain: 'Website domains',
  notificationEmail: 'Notification emails',
  personalityPrompt: 'Personality prompt',
  basicKnowledge: 'Basic knowledge',
};

export function createInitialOnboardingAssistantFieldSyncState(): OnboardingAssistantFieldSyncState {
  return ONBOARDING_ASSISTANT_SYNC_FIELDS.reduce(
    (acc, field) => {
      acc[field] = { source: 'initial' };
      return acc;
    },
    {} as OnboardingAssistantFieldSyncState,
  );
}

export function isOnboardingAssistantSyncField(
  value: string,
): value is OnboardingAssistantSyncField {
  return (ONBOARDING_ASSISTANT_SYNC_FIELDS as readonly string[]).includes(value);
}

export function pickOnboardingAssistantFieldPatch(
  input: unknown,
): OnboardingAssistantFieldPatch {
  if (!input || typeof input !== 'object') {
    return {};
  }

  const patch: OnboardingAssistantFieldPatch = {};

  for (const [field, value] of Object.entries(input as Record<string, unknown>)) {
    if (!isOnboardingAssistantSyncField(field)) {
      continue;
    }

    if (typeof value === 'string') {
      patch[field] = value;
      continue;
    }

    if (value !== null && value !== undefined) {
      patch[field] = String(value);
    }
  }

  return patch;
}

export function buildOnboardingAssistantContext(
  formData: AgentBuilderFormData,
  syncState: OnboardingAssistantFieldSyncState,
  options?: {
    activeStepId?: string;
    effectiveModelId?: string;
    availableModelIds?: string[];
  },
): OnboardingAssistantContext {
  const fields = ONBOARDING_ASSISTANT_SYNC_FIELDS.reduce(
    (acc, field) => {
      const value = String(formData[field] ?? '');
      const source = syncState[field]?.source ?? 'initial';
      acc[field] = {
        label: ONBOARDING_ASSISTANT_FIELD_LABELS[field],
        value,
        source,
        lockedByManualInput: source === 'manual' && value.trim().length > 0,
      };
      return acc;
    },
    {} as Record<OnboardingAssistantSyncField, OnboardingAssistantContextField>,
  );

  return {
    onboarding_form: {
      form_id: AGENT_BUILDER_ONBOARDING_FORM_ID,
      surface: 'agent_builder_step2',
      active_step_id: options?.activeStepId ?? 'craft',
      supported_fields: [...ONBOARDING_ASSISTANT_SYNC_FIELDS],
      fields,
      formatting_rules: {
        websiteDomain:
          'Use a comma-separated list of domains without protocol or path, for example example.com, docs.example.com.',
        notificationEmail:
          'Use a comma-separated list of email addresses, for example ops@example.com, founders@example.com.',
      },
      effective_model_id: options?.effectiveModelId,
      available_model_ids: options?.availableModelIds,
    },
  };
}
